import React, { useEffect, useState } from 'react';
import { Card, Button, message, Typography, Space, Spin, Statistic, Row, Col } from 'antd';
import { useSupabaseAuth } from '@/utils/store/supabaseAuth';
import { ApiService } from '@/services/api';
import { handleError } from '@/utils/ui-helpers';
import { RecordingDurationStore } from '@/utils/store/recordingDuration';
import { fetchRecordingDurationConfigAndValue } from '@/services/recordingDurationService';

const { Title, Text } = Typography;

const SuperAdminPage: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currentDuration, setCurrentDuration] = useState<number>(0);
  const [maxDuration, setMaxDuration] = useState<number>(0);

  // 格式化时长显示
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}小时${minutes}分${secs}秒`;
    }
    return `${minutes}分${secs}秒`;
  };

  // 获取录音时长配置
  const fetchDuration = async () => {
    if (!user?.id) {
      return;
    }

    setFetching(true);
    try {
      const config = await fetchRecordingDurationConfigAndValue(user.id);
      if (config) {
        setCurrentDuration(config.currentUsage);
        setMaxDuration(config.maxLimit);
      }
    } catch (error) {
      handleError(error, '获取录音时长失败', 'superadmin');
    } finally {
      setFetching(false);
    }
  };

  // 页面加载时获取录音时长
  useEffect(() => {
    if (user?.id) {
      fetchDuration();
    }
  }, [user?.id]);

  const handleResetRecordingDuration = async () => {
    if (!user?.id) {
      message.error('用户未登录');
      return;
    }

    setLoading(true);
    try {
      await ApiService.resetRecordingDuration(user.id);
      message.success('录音时长已清空');
      
      // 更新本地状态
      setCurrentDuration(0);
      
      // 更新 valtio store
      RecordingDurationStore.updateCurrentDuration(0);
      
      // 重新获取配置以同步最新数据
      await fetchDuration();
    } catch (error) {
      handleError(error, '清空录音时长失败', 'superadmin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Title level={3} className="mb-2">
          超级管理员
        </Title>
        <Text type="secondary">
          管理员相关功能
        </Text>
      </div>

      <Card title="录音时长管理" className="mb-4">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>当前用户ID：</Text>
            <Text className="ml-2">{user?.id || '未登录'}</Text>
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title="已使用时长"
                value={fetching ? '-' : formatDuration(currentDuration)}
                loading={fetching}
                prefix={<Spin spinning={false} size="small" />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="时长上限"
                value={fetching ? '-' : formatDuration(maxDuration)}
                loading={fetching}
              />
            </Col>
          </Row>

          <div>
            <Text>清空当前用户的录音时长使用量</Text>
          </div>

          <Space>
            <Button
              onClick={fetchDuration}
              loading={fetching}
              disabled={!user?.id}
            >
              刷新时长
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleResetRecordingDuration}
              loading={loading}
              disabled={!user?.id}
            >
              {loading ? '处理中...' : '清空录音时长'}
            </Button>
          </Space>
        </Space>
      </Card>
    </div>
  );
};

export default SuperAdminPage;